import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Download, FileText, Printer } from "lucide-react";

export default function Waiver() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [template, setTemplate] = useState(null);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({ full_name: user?.name || "", date_of_birth: "", emergency_contact_name: "", emergency_contact_phone: "" });
  const [agreed, setAgreed] = useState([]);
  const [readWaiver, setReadWaiver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [minorBlocked, setMinorBlocked] = useState(false);
  const [minorAge, setMinorAge] = useState(null);
  const [minorAck, setMinorAck] = useState(false);
  const [minorContinuing, setMinorContinuing] = useState(false);

  useEffect(() => {
    api.get("/waiver/template").then(({ data }) => {
      setTemplate(data);
      setAgreed(new Array(data.clauses.length).fill(false));
    });
    api.get("/waiver/me").then(({ data }) => {
      if (data?.signed) setExisting(data);
      // Prefill DOB (saved on the user doc at registration or earlier waiver
      // attempts) so volunteers who already told us their birthday see the
      // right inline flow immediately — no re-typing required.
      if (data?.dob || data?.date_of_birth) {
        setForm((f) => ({ ...f, date_of_birth: data.dob || data.date_of_birth || f.date_of_birth }));
      }
    });
  }, []);

  const downloadReferencePdf = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/waiver/reference-pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("not found");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hraad-volunteer-waiver.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Paper waiver downloaded — print it and have a parent/guardian sign.");
    } catch {
      toast.error("Paper waiver PDF isn't available. Please contact the shelter.");
    }
  };

  // Open the paper waiver in a new tab and auto-trigger the print dialog so
  // volunteers (especially under-18 users with a parent/guardian) can print
  // without having to save-then-open the file.
  const printReferencePdf = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/waiver/reference-pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("not found");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        toast.error("Pop-up blocked — allow pop-ups or use Download instead.");
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        return;
      }
      // Give the browser a moment to render the PDF viewer, then invoke print.
      // Some browsers (Safari) ignore programmatic print() on blob PDFs — the
      // user can still hit Cmd/Ctrl-P in the new tab, which is why we also
      // show the Download fallback alongside.
      const tryPrint = () => { try { win.focus(); win.print(); } catch (_) {} };
      win.addEventListener("load", tryPrint);
      setTimeout(tryPrint, 1500);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Couldn't open the waiver for printing. Try the Download button.");
    }
  };

  const clauses = template?.clauses || [];
  const allChecked = clauses.length > 0 && agreed.length === clauses.length && agreed.every(Boolean);
  const dobValid = /^\d{4}-\d{2}-\d{2}$/.test(form.date_of_birth || "");
  const canSubmit = allChecked
    && readWaiver
    && dobValid
    && form.full_name.trim().length > 1
    && form.emergency_contact_name.trim().length > 0
    && form.emergency_contact_phone.trim().length > 4;

  const viewReferencePdf = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/waiver/reference-pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("not found");
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      toast.error("Reference PDF is not available.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    // Minor DOB — the inline paper-waiver block handles continuation; stop
    // here so we never POST a signed waiver for an under-18 volunteer.
    const age = ageFromDob(form.date_of_birth);
    if (age >= 0 && age < 18) {
      toast.error("Under 18 — please use the paper waiver below.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/waiver/sign", { ...form, signed: true });
      toast.success("Waiver signed. You're cleared to volunteer!");
      nav("/shifts");
    } catch (er) {
      toast.error(formatApiError(er));
    } finally { setBusy(false); }
  };

  const ageFromDob = (dob) => {
    if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(dob)) return -1;
    const [y, m, d] = dob.split("-").map(Number);
    const t = new Date();
    let a = t.getFullYear() - y;
    if (t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < d)) a -= 1;
    return a;
  };

  if (minorBlocked) {
    return null;
  }

  if (existing && !existing.is_expired) {
    const downloadPdf = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${BACKEND_URL}/api/waiver/pdf`, { credentials: "include" });
        if (!res.ok) throw new Error("Download failed");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `hraad-waiver-${user?.name?.replace(/\s+/g, "-") || "signed"}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Waiver downloaded.");
      } catch (e) {
        toast.error("Could not download the waiver. Please try again.");
      }
    };
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-16">
          <div className="card-warm p-10 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#E4EBDD] flex items-center justify-center text-[#2D4A3E] mb-4">
              <ShieldCheck className="h-8 w-8" />
            </div>
            <h1 className="font-display text-3xl mb-2">You&rsquo;re all signed.</h1>
            <p className="text-[color:var(--stone)]">Signed on {new Date(existing.signed_at).toLocaleDateString()} by <span className="font-semibold">{existing.full_name}</span>.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button data-testid="waiver-download-pdf-btn" onClick={downloadPdf} className="btn-pill btn-ghost-ink text-sm inline-flex items-center gap-2">
                <Download className="h-4 w-4" /> Download PDF
              </button>
              <button data-testid="waiver-go-shifts-btn" onClick={()=>nav("/shifts")} className="btn-pill btn-primary text-sm">Go to open shifts</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 lg:px-10 py-10">
        <div className="tag-pill tag-red">{existing?.is_expired ? "Waiver expired — re-sign" : "One-time waiver"}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 mb-2">{template?.title || "Volunteer liability waiver"}</h1>
        {existing?.is_expired && (
          <div className="mb-4 p-4 rounded-2xl bg-[color:var(--red)]/10 border-l-4 border-l-[color:var(--red)] text-sm" data-testid="waiver-expired-banner">
            <span className="font-semibold">Your previous waiver expired on {new Date(existing.expires_at).toLocaleDateString()}.</span> Please re-sign to keep volunteering.
          </div>
        )}
        <p className="text-[color:var(--stone)] mb-4">{template?.intro || "Loading…"}</p>
        {template?.has_reference_pdf && (
          <button data-testid="waiver-reference-pdf-btn" onClick={viewReferencePdf} className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--red)] hover:underline">
            <FileText className="h-4 w-4" /> View full waiver PDF{template?.reference_pdf_filename ? ` (${template.reference_pdf_filename})` : ""}
          </button>
        )}

        <form onSubmit={submit} className="card-warm p-8 space-y-5">
          {/* DOB first so minor/adult branches resolve before anything else renders.
              Once the user has a DOB on file we lock the input so volunteers can't
              edit it to flip from minor to adult. */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] ml-1">Date of birth</label>
            <input
              data-testid="waiver-dob-input"
              type="date"
              required
              value={form.date_of_birth}
              max={new Date().toISOString().split("T")[0]}
              readOnly={!!user?.date_of_birth}
              disabled={!!user?.date_of_birth}
              onChange={(e)=>setForm({...form, date_of_birth: e.target.value})}
              className={`mt-1 w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white ${user?.date_of_birth ? "cursor-not-allowed opacity-70" : ""}`}
            />
            {user?.date_of_birth && (
              <p className="mt-1 ml-1 text-[11px] text-[color:var(--stone)]" data-testid="waiver-dob-locked-note">
                On file. Ask an admin to correct it if this isn&rsquo;t right.
              </p>
            )}
            {dobValid && ageFromDob(form.date_of_birth) >= 0 && ageFromDob(form.date_of_birth) < 18 && (
              <p className="mt-2 ml-1 text-xs text-[color:var(--red)] font-semibold" data-testid="waiver-dob-minor-warning">
                Under 18 — you'll need a parent or guardian to sign the paper waiver below. You can't sign the waiver online.
              </p>
            )}
          </div>

          {dobValid && ageFromDob(form.date_of_birth) >= 0 && ageFromDob(form.date_of_birth) < 18 ? (
            // Minor branch: inline paper-waiver block — stays on the same page.
            <div data-testid="waiver-minor-inline" className="rounded-2xl bg-[color:var(--cream-2)] p-5 space-y-4">
              <div className="tag-pill tag-red">Paper waiver required</div>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-[color:var(--ink)]">
                <li><span className="font-semibold">Print</span> the paper waiver (or Download it to print later).</li>
                <li><span className="font-semibold">Have your parent or guardian</span> read and sign both signature blocks.</li>
                <li><span className="font-semibold">Bring the signed paper to your first shift</span> at 3910 Heron Drive, Hood River, OR 97031 — the team will scan it in on arrival.</li>
              </ol>
              <div className="flex flex-wrap gap-3">
                <button type="button" data-testid="waiver-minor-print-btn" onClick={printReferencePdf} className="btn-pill btn-primary text-sm inline-flex items-center gap-2">
                  <Printer className="h-4 w-4" /> Print paper waiver
                </button>
                <button type="button" data-testid="waiver-minor-download-btn" onClick={downloadReferencePdf} className="btn-pill btn-ghost-ink text-sm inline-flex items-center gap-2">
                  <Download className="h-4 w-4" /> Download instead
                </button>
              </div>
              <label className="flex items-start gap-3 p-4 rounded-2xl bg-white cursor-pointer" data-testid="waiver-minor-ack-label">
                <input
                  type="checkbox"
                  data-testid="waiver-minor-ack-checkbox"
                  checked={minorAck}
                  onChange={(e) => setMinorAck(e.target.checked)}
                  className="h-5 w-5 mt-0.5 accent-[color:var(--red)]"
                />
                <span className="text-sm text-[color:var(--ink)]">
                  (Under-18 volunteers must have a parent or guardian sign a paper copy in person.)
                </span>
              </label>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  data-testid="waiver-minor-continue-btn"
                  disabled={!minorAck || minorContinuing}
                  onClick={async () => {
                    setMinorContinuing(true);
                    try {
                      await api.post("/waiver/dob", { date_of_birth: form.date_of_birth }).catch(() => {});
                      await api.post("/waiver/minor-acknowledge");
                      toast.success("Great — browse open shifts and bring your signed paper on the day.");
                      nav("/shifts");
                    } catch (e) {
                      toast.error(formatApiError(e));
                    } finally { setMinorContinuing(false); }
                  }}
                  className="btn-pill btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {minorContinuing ? "Saving…" : "Continue to shifts"}
                </button>
                <button type="button" data-testid="waiver-minor-back-btn" onClick={()=>nav("/dashboard")} className="btn-pill btn-ghost-ink text-sm">Back to dashboard</button>
              </div>
            </div>
          ) : (
            // Adult branch: existing online waiver flow.
            <>
              {clauses.map((t, i) => (
                <label key={`${i}-${t.slice(0,32)}`} className="flex gap-3 items-start cursor-pointer" data-testid={`waiver-clause-${i}`}>
                  <input
                    data-testid={`waiver-check-${i}`}
                    type="checkbox"
                    checked={agreed[i] || false}
                    onChange={(e)=> {
                      const c = [...agreed]; c[i] = e.target.checked; setAgreed(c);
                    }}
                    className="mt-1 h-5 w-5 rounded border-2 border-[color:var(--ink)] accent-[color:var(--red)]"
                  />
                  <span className="text-sm text-[color:var(--ink)]">{t}</span>
                </label>
              ))}

              <hr className="border-[color:var(--border)]" />

              <label className="flex gap-3 items-start cursor-pointer p-4 rounded-2xl bg-[color:var(--yellow)]/25" data-testid="waiver-read-confirm">
                <input
                  data-testid="waiver-read-checkbox"
                  type="checkbox"
                  checked={readWaiver}
                  onChange={(e)=>setReadWaiver(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-2 border-[color:var(--ink)] accent-[color:var(--red)]"
                />
                <span className="text-sm font-semibold text-[color:var(--ink)]">I confirm I have read the entire waiver above{template?.has_reference_pdf ? " (including the full PDF)" : ""}.</span>
              </label>

              <div className="grid md:grid-cols-2 gap-3">
                <input data-testid="waiver-emergency-name" required placeholder="Emergency contact name (required)" value={form.emergency_contact_name} onChange={(e)=>setForm({...form, emergency_contact_name: e.target.value})} className="px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white" />
                <input data-testid="waiver-emergency-phone" required placeholder="Emergency contact phone (required)" value={form.emergency_contact_phone} onChange={(e)=>setForm({...form, emergency_contact_phone: e.target.value})} className="px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white" />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)]">Type your full legal name to sign</label>
                <input
                  data-testid="waiver-signature-input"
                  value={form.full_name}
                  onChange={(e)=>setForm({...form, full_name: e.target.value})}
                  placeholder="Your full name"
                  className="mt-2 w-full px-4 py-4 rounded-2xl border-2 border-dashed border-[color:var(--ink)] font-serif-display text-2xl italic bg-white"
                />
              </div>

              <div className="text-xs text-[color:var(--stone)]">By clicking Sign, I acknowledge this is my digital signature and holds the same weight as a handwritten one.</div>

              <button data-testid="waiver-submit-btn" disabled={!canSubmit || busy} className="w-full btn-pill btn-primary disabled:opacity-50">{busy ? "Signing…" : "Sign waiver"}</button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
